import React, { useState, useEffect } from 'react';
import { PersonFinal } from '@/api/entities';
import { AccountFinal } from '@/api/entities';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Loader2, CheckCircle, AlertTriangle, Link as LinkIcon, Search, Filter } from 'lucide-react';
import { toast } from 'sonner';

const normalizeAccountName = (name) => {
  if (!name) return '';
  return name.trim().toLowerCase().replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();
};

export default function PersonaAccountReconciler() {
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  
  // Filters
  const [tenantFilter, setTenantFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [issueTypeFilter, setIssueTypeFilter] = useState('all');
  
  // Selection
  const [selectedPersonas, setSelectedPersonas] = useState(new Set());

  const analyzePersonas = async () => {
    setIsLoading(true);
    setSelectedPersonas(new Set()); // Clear selection on re-analyze
    
    try {
      const [allPeople, allAccounts] = await Promise.all([
        PersonFinal.filter({}),
        AccountFinal.filter({})
      ]);

      console.log('🔍 Loaded:', { people: allPeople.length, accounts: allAccounts.length });
      
      // Apply tenant filtering if a specific tenant is selected
      const tenantMatches = (record, tenantId) => {
        if (!tenantId || tenantId === 'all') return true;
        if (!record) return false;
        if (tenantId === 'smowl') return record.tenant_id === 'smowl';
        if (record.tenant_id === tenantId || record.tenant_id === 'global') return true;
        if (tenantId === 'instructure' && (record.tenant_id === 'default' || !record.tenant_id)) return true;
        if (Array.isArray(record.visible_to_tenants) && record.visible_to_tenants.includes(tenantId)) return true;
        if (record.tenant_overrides && record.tenant_overrides[tenantId]) return true;
        if (tenantId === 'diambra' && (record.tenant_id === 'smowl' || record.tenant_id === 'instructure' || record.tenant_id === 'clairelabs' || record.tenant_id === 'base44' || record.tenant_id === 'default')) return true;
        if (tenantId === 'edqwest') return record.tenant_id === 'edqwest';
        if (tenantId === 'faria') return record.tenant_id === 'edqwest';
        if (tenantId === 'calmedica') return record.tenant_id === 'calmedica' || record.tenant_id === 'global';
        if (tenantId === 'sebit') return record.tenant_id === 'sebit' || record.tenant_id === 'global';
        if (tenantId === 'prowise') return record.tenant_id === 'prowise' || record.tenant_id === 'global';
        return false;
      };
      
      const filteredPeople = tenantFilter === 'all' 
        ? allPeople 
        : allPeople.filter(p => tenantMatches(p, tenantFilter));
      
      const filteredAccounts = tenantFilter === 'all'
        ? allAccounts
        : allAccounts.filter(a => tenantMatches(a, tenantFilter));
      
      console.log('🔍 After tenant filter:', { 
        people: filteredPeople.length, 
        accounts: filteredAccounts.length,
        tenant: tenantFilter,
        samplePeople: filteredPeople.slice(0, 5).map(p => ({ 
          name: `${p.first_name} ${p.last_name}`, 
          account: p.account_name,
          tenant: p.tenant_id 
        })),
        sampleAccounts: filteredAccounts.slice(0, 5).map(a => ({ 
          name: a.account_name,
          tenant: a.tenant_id 
        }))
      });

      // Create lookup map by database ID, account_id field, and normalized name
      const accountsByDbId = new Map();
      const accountsByAccountId = new Map();
      const accountsByName = new Map();
      
      filteredAccounts.forEach(acc => {
        // Map by database ID
        if (acc.id) {
          accountsByDbId.set(acc.id, acc);
        }
        // Map by account_id field
        if (acc.account_id) {
          accountsByAccountId.set(acc.account_id, acc);
        }
        // Map by normalized name
        const normalized = normalizeAccountName(acc.account_name);
        if (normalized) {
          accountsByName.set(normalized, acc);
        }
      });
      
      console.log('📋 Account lookup maps:', {
        accountsByDbIdSize: accountsByDbId.size,
        accountsByAccountIdSize: accountsByAccountId.size,
        accountsByNameSize: accountsByName.size,
        sampleNames: Array.from(accountsByName.keys()).slice(0, 10),
        tenant: tenantFilter,
        filteredAccountsCount: filteredAccounts.length,
        allAccountsCount: allAccounts.length
      });

      // Analyze personas
      const issues = [];

      for (const person of filteredPeople) {
        if (!person.account_name) continue;

        const normalizedName = normalizeAccountName(person.account_name);
        
        // Try multiple matching strategies
        let matchingAccount = accountsByName.get(normalizedName);
        
        // If no match by name, try by person's account_id
        if (!matchingAccount && person.account_id) {
          matchingAccount = accountsByDbId.get(person.account_id) || accountsByAccountId.get(person.account_id);
        }

        if (!matchingAccount) {
          // Only log if tenant filter matches or is 'all'
          if (tenantFilter === 'all' || person.tenant_id === tenantFilter) {
            console.log('❌ No account match for:', person.account_name, {
              normalizedName,
              personTenant: person.tenant_id,
              filteringByTenant: tenantFilter,
              totalFilteredAccounts: filteredAccounts.length,
              sampleFilteredAccountNames: filteredAccounts.slice(0, 5).map(a => a.account_name)
            });
          }
          continue;
        }

        // Check if account_id is missing or wrong (should point to database ID)
        let issueType = null;
        if (!person.account_id) {
          issueType = 'missing';
        } else if (person.account_id !== matchingAccount.id) {
          // The person.account_id should match the account's database ID (matchingAccount.id)
          issueType = 'wrong';
        }

        if (issueType) {
          issues.push({
            person,
            issueType,
            personId: person.id,
            personName: `${person.first_name} ${person.last_name}`,
            accountName: person.account_name,
            currentAccountId: person.account_id || 'N/A',
            correctAccountId: matchingAccount.id,
            tenantId: person.tenant_id || 'default'
          });
        }
      }

      // Get unique tenants from ALL loaded data, not just issues
      const allTenants = new Set();
      filteredPeople.forEach(p => allTenants.add(p.tenant_id || 'default'));
      const uniqueTenants = [...allTenants].sort();

      setAnalysis({
        totalPeople: filteredPeople.length,
        totalAccounts: filteredAccounts.length,
        totalIssues: issues.length,
        issues,
        uniqueTenants,
        accountsByDbId,
        accountsByName
      });

      console.log('📊 Analysis:', {
        total: filteredPeople.length,
        issues: issues.length,
        tenants: uniqueTenants,
        selectedTenant: tenantFilter
      });

    } catch (error) {
      console.error('Error analyzing personas:', error);
      toast.error('Failed to analyze personas');
    } finally {
      setIsLoading(false);
    }
  };

  const filteredIssues = analysis?.issues.filter(issue => {
    // Tenant filter
    if (tenantFilter !== 'all' && issue.tenantId !== tenantFilter) return false;
    
    // Issue type filter
    if (issueTypeFilter !== 'all' && issue.issueType !== issueTypeFilter) return false;
    
    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      return (
        issue.personName.toLowerCase().includes(search) ||
        issue.accountName.toLowerCase().includes(search)
      );
    }
    
    return true;
  }) || [];

  const togglePersona = (personId) => {
    const newSelected = new Set(selectedPersonas);
    if (newSelected.has(personId)) {
      newSelected.delete(personId);
    } else {
      newSelected.add(personId);
    }
    setSelectedPersonas(newSelected);
  };

  const toggleAll = () => {
    if (selectedPersonas.size === filteredIssues.length) {
      setSelectedPersonas(new Set());
    } else {
      setSelectedPersonas(new Set(filteredIssues.map(i => i.personId)));
    }
  };

  const fixSelectedPersonas = async () => {
    if (selectedPersonas.size === 0) {
      toast.error('No personas selected');
      return;
    }

    const confirmed = window.confirm(
      `This will update ${selectedPersonas.size} persona records. Continue?`
    );
    if (!confirmed) return;

    setIsProcessing(true);
    setProgress({ current: 0, total: selectedPersonas.size });

    let successCount = 0;
    let errorCount = 0;
    const errors = [];

    const selectedIssues = analysis.issues.filter(i => selectedPersonas.has(i.personId));

    for (let i = 0; i < selectedIssues.length; i++) {
      const issue = selectedIssues[i];
      
      try {
        const normalizedName = normalizeAccountName(issue.accountName);
        const matchingAccount = analysis.accountsByName.get(normalizedName);

        if (matchingAccount) {
          const updateData = {
            account_id: matchingAccount.id
          };
          
          // Also update tenant_id to match the account's tenant_id
          if (matchingAccount.tenant_id && 
              (matchingAccount.tenant_id === 'prowise' || 
               matchingAccount.tenant_id === 'sebit' ||
               matchingAccount.tenant_id === 'calmedica' ||
               matchingAccount.tenant_id === 'edqwest' ||
               matchingAccount.tenant_id === 'faria')) {
            updateData.tenant_id = matchingAccount.tenant_id;
          }
          
          await PersonFinal.update(issue.personId, updateData);
          successCount++;
          console.log(`✅ Fixed: ${issue.personName} → ${matchingAccount.account_name}`, updateData);
        }
      } catch (error) {
        errorCount++;
        errors.push(`${issue.personName}: ${error.message}`);
        console.error(`❌ Failed to fix ${issue.personName}:`, error);
      }

      setProgress({ current: i + 1, total: selectedPersonas.size });
    }

    setIsProcessing(false);
    
    toast.success(`Fixed ${successCount} personas! ${errorCount > 0 ? `${errorCount} errors.` : ''}`);
    
    // Clear selection and re-analyze
    setSelectedPersonas(new Set());
    await analyzePersonas();
  };

  useEffect(() => {
    analyzePersonas();
  }, [tenantFilter]);

  const stats = {
    missing: filteredIssues.filter(i => i.issueType === 'missing').length,
    wrong: filteredIssues.filter(i => i.issueType === 'wrong').length
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <LinkIcon className="w-5 h-5" />
          Persona-Account Reconciler
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            <span className="ml-2 text-sm text-gray-600">Analyzing personas...</span>
          </div>
        ) : analysis ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{analysis.totalPeople}</div>
                <div className="text-xs text-gray-600">Total Personas</div>
              </div>
              <div className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold text-gray-900">{analysis.totalIssues}</div>
                <div className="text-xs text-gray-600">Total Issues</div>
              </div>
              <div className="text-center p-4 bg-orange-50 rounded-lg">
                <div className="text-2xl font-bold text-orange-600">{stats.missing}</div>
                <div className="text-xs text-orange-700">Missing (filtered)</div>
              </div>
              <div className="text-center p-4 bg-red-50 rounded-lg">
                <div className="text-2xl font-bold text-red-600">{stats.wrong}</div>
                <div className="text-xs text-red-700">Wrong (filtered)</div>
              </div>
            </div>

            {analysis.totalIssues > 0 ? (
              <>
                {/* Filters */}
                <div className="flex flex-col md:flex-row gap-3 p-4 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                      <Input
                        placeholder="Search by name or account..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 text-sm"
                      />
                    </div>
                  </div>
                  
                  <Select value={tenantFilter} onValueChange={setTenantFilter}>
                    <SelectTrigger className="w-full md:w-48">
                      <SelectValue placeholder="Filter by tenant" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Tenants</SelectItem>
                      {analysis.uniqueTenants.map(tenant => (
                        <SelectItem key={tenant} value={tenant}>
                          {tenant || 'default'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={issueTypeFilter} onValueChange={setIssueTypeFilter}>
                    <SelectTrigger className="w-full md:w-48">
                      <SelectValue placeholder="Issue type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Issues</SelectItem>
                      <SelectItem value="missing">Missing ID</SelectItem>
                      <SelectItem value="wrong">Wrong ID</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button 
                    onClick={analyzePersonas}
                    variant="outline"
                    size="sm"
                    disabled={isProcessing}
                  >
                    Refresh
                  </Button>
                </div>

                {/* Selection Summary */}
                {selectedPersonas.size > 0 && (
                  <Alert>
                    <CheckCircle className="h-4 w-4" />
                    <AlertDescription>
                      <strong>{selectedPersonas.size}</strong> persona{selectedPersonas.size !== 1 ? 's' : ''} selected
                    </AlertDescription>
                  </Alert>
                )}

                {/* Issues Table */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="max-h-96 overflow-y-auto">
                    <Table>
                      <TableHeader className="bg-gray-50 sticky top-0">
                        <TableRow>
                          <TableHead className="w-12">
                            <Checkbox
                              checked={selectedPersonas.size === filteredIssues.length && filteredIssues.length > 0}
                              onCheckedChange={toggleAll}
                            />
                          </TableHead>
                          <TableHead>Person</TableHead>
                          <TableHead>Account</TableHead>
                          <TableHead>Tenant</TableHead>
                          <TableHead>Issue</TableHead>
                          <TableHead>Current ID</TableHead>
                          <TableHead>Correct ID</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredIssues.map((issue) => (
                          <TableRow key={issue.personId}>
                            <TableCell>
                              <Checkbox
                                checked={selectedPersonas.has(issue.personId)}
                                onCheckedChange={() => togglePersona(issue.personId)}
                              />
                            </TableCell>
                            <TableCell className="font-medium">
                              {issue.personName}
                            </TableCell>
                            <TableCell className="text-sm text-gray-600">
                              {issue.accountName}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">
                                {issue.tenantId || 'default'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge 
                                variant="outline" 
                                className={issue.issueType === 'missing' ? 'bg-orange-50 text-orange-700' : 'bg-red-50 text-red-700'}
                              >
                                {issue.issueType === 'missing' ? 'Missing' : 'Wrong'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-xs text-gray-500 font-mono">
                              {issue.currentAccountId}
                            </TableCell>
                            <TableCell className="text-xs text-green-600 font-mono">
                              {issue.correctAccountId.slice(0, 20)}...
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>

                {filteredIssues.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No personas match the current filters
                  </div>
                )}

                {/* Action Buttons */}
                {isProcessing ? (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span>Processing...</span>
                      <span>{progress.current} / {progress.total}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                        style={{ width: `${(progress.current / progress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button 
                      onClick={fixSelectedPersonas}
                      className="bg-blue-600 hover:bg-blue-700"
                      disabled={selectedPersonas.size === 0 || isProcessing}
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Fix Selected ({selectedPersonas.size})
                    </Button>
                    <Button 
                      onClick={() => setSelectedPersonas(new Set(filteredIssues.map(i => i.personId)))}
                      variant="outline"
                      disabled={filteredIssues.length === 0}
                    >
                      Select All Filtered ({filteredIssues.length})
                    </Button>
                    <Button 
                      onClick={() => setSelectedPersonas(new Set())}
                      variant="outline"
                      disabled={selectedPersonas.size === 0}
                    >
                      Clear Selection
                    </Button>
                  </div>
                )}
              </>
            ) : (
              <Alert>
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-700">
                  All personas are correctly linked to accounts! 🎉
                </AlertDescription>
              </Alert>
            )}
          </>
        ) : null}
      </CardContent>
    </Card>
  );
}